import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext,
} from 'react';

import { isMobileDevice } from 'src/utils/device';
import { startExtensionQueue } from 'src/utils/extension-queue';
import { trackEvent, ANALYTICS_EVENTS } from 'src/utils/posthog';
import { askExtensionIfItCanAct } from 'src/utils/extension-availability';

import { getBatchStatus } from 'src/redux/slices/bulk-cross-list';
import { reloadListingsAfterChange } from 'src/redux/slices/listing';

import { useExtensionRunner } from '../hooks/use-extension-runner';
import { pruneExtensionProgress } from '../utils/extension-progress-log';
import { saveBatchSnapshot, readBatchSnapshot, pruneBatchSnapshots } from '../utils/batch-snapshot';
import {
  isCleanRun,
  countRunResults,
  deriveRunStatus,
  leftUpdatesQueued,
  composeInspectedRun,
  mergeExtensionResults,
  listRetryableFailures,
} from '../utils/run-results';

/**
 * Owns the one bulk cross-listing run the user has going: it polls the batch on the server,
 * drives the Chrome extension through whatever the server could not create itself, and hands
 * the combined picture to whoever wants to render it.
 *
 * There is deliberately one of these for the whole app. Any number of views can show the same
 * run, and none of them can start a second extension loop over the same listings.
 */

const POLL_INTERVAL_MS = 2000;

// Once the batch reaches one of these, the server has nothing left to tell us.
const TERMINAL_SERVER_STATUSES = ['completed', 'not_found'];

// The run the user has open survives a reload, but not indefinitely: a day-old run is history,
// not something to reopen a progress widget for.
const ACTIVE_RUN_STORAGE_KEY = 'bulk_cross_list_active_run';
const ACTIVE_RUN_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const emptyProgress = { current: 0, total: 0, currentListing: '', currentPlatform: '' };

function readStoredRun() {
  try {
    const raw = localStorage.getItem(ACTIVE_RUN_STORAGE_KEY);
    if (!raw) return null;

    const stored = JSON.parse(raw);
    if (!stored?.batchId) return null;

    const submittedAt = new Date(stored.submittedAt).getTime();
    if (!submittedAt || Date.now() - submittedAt > ACTIVE_RUN_MAX_AGE_MS) return null;

    return stored;
  } catch (e) {
    return null;
  }
}

function writeStoredRun(run) {
  try {
    if (run) localStorage.setItem(ACTIVE_RUN_STORAGE_KEY, JSON.stringify(run));
    else localStorage.removeItem(ACTIVE_RUN_STORAGE_KEY);
  } catch (e) {
    // A run that cannot be remembered still works for as long as this page is open.
  }
}

// Analytics must hear about one run finishing exactly once, no matter how many times the page
// polls or reloads while that run is still the active one: a seller who reloads a completed run
// within its active window sees the same 'completed' status again, and that is not a new
// completion. `activeRun.completionReported` is what tells the two apart, and it is carried with
// the rest of the run's stored state, so it survives the reload that would otherwise repeat it.
function isNewCompletionSighting(activeRun, payloadStatus) {
  return payloadStatus === 'completed' && !activeRun?.completionReported;
}


// Every (listing, platform) the server left for the extension to create.
function collectPendingExtensionItems(serverStatus) {
  return (serverStatus?.results || []).flatMap((result) =>
    Object.entries(result.results || {})
      .filter(([, platformResult]) => platformResult.pendingExtension)
      .map(([platform]) => ({
        listingId: result.listingId,
        listingTitle: result.listingTitle,
        platform,
      }))
  );
}

const BulkCrossListRunContext = createContext(null);

export function useBulkCrossListRun() {
  const context = useContext(BulkCrossListRunContext);
  if (!context) {
    throw new Error('useBulkCrossListRun must be used within a BulkCrossListRunProvider');
  }
  return context;
}

export function BulkCrossListRunProvider({ children }) {
  const dispatch = useDispatch();

  const [activeRun, setActiveRun] = useState(readStoredRun);
  const [serverStatus, setServerStatus] = useState(null);
  // Where what we are showing came from: the server right now, the last snapshot we kept of
  // this batch, or nothing at all because both are gone.
  const [source, setSource] = useState('live');
  const [extensionResults, setExtensionResults] = useState({});
  const [extensionProgress, setExtensionProgress] = useState(emptyProgress);
  // Set when the extension did not answer, so the run's own page can say what is still waiting
  // and offer to try again. Null whenever there is nothing to say.
  const [extensionBlocked, setExtensionBlocked] = useState(null);
  // Marketplaces that refused this run as automation. The run stopped sending them anything more,
  // so their share of it was never attempted and is shown as such rather than as failed.
  const [refusedAsAutomation, setRefusedAsAutomation] = useState([]);

  const batchId = activeRun?.batchId || null;
  // The seller stopped this run. Kept with the run (and with its snapshot) so it survives a
  // reload: what was not created stays not created, and nothing starts creating it again.
  const isStopped = Boolean(activeRun?.stoppedAt);

  // Read by callbacks and by the poll timer, which must not be rebuilt every time these change.
  const serverStatusRef = useRef(serverStatus);
  const extensionResultsRef = useRef(extensionResults);
  const activeRunRef = useRef(activeRun);
  useEffect(() => { serverStatusRef.current = serverStatus; }, [serverStatus]);
  useEffect(() => { extensionResultsRef.current = extensionResults; }, [extensionResults]);
  useEffect(() => { activeRunRef.current = activeRun; }, [activeRun]);

  // Per-batch latches.
  const extensionTriggeredRef = useRef(false);
  const queueWokenRef = useRef(false);
  const listingsRefreshedRef = useRef(false);
  const completeModalShownRef = useRef(false);
  const lastSnapshotRef = useRef({ signature: null, extensionResults: null });

  const handleExtensionResult = useCallback((listingId, platform, result) => {
    setExtensionResults((current) => ({
      ...current,
      [listingId]: { ...(current[listingId] || {}), [platform]: result },
    }));
  }, []);

  const handleAutomationBlock = useCallback((platform) => {
    setRefusedAsAutomation((current) => (current.includes(platform) ? current : [...current, platform]));
  }, []);

  const {
    start: startExtensionRun,
    retry: retryExtensionItem,
    cancel: stopExtensionRun,
    pause: pauseExtension,
    resume: resumeExtension,
    phase: extensionPhase,
    isPaused: extensionPaused,
  } = useExtensionRunner({
    batchId,
    onProgress: setExtensionProgress,
    onResult: handleExtensionResult,
    onAutomationBlock: handleAutomationBlock,
  });

  // ── Keeping the run ───────────────────────────────────────────────────────────────────────

  useEffect(() => {
    pruneBatchSnapshots();
    pruneExtensionProgress();
  }, []);

  useEffect(() => {
    writeStoredRun(activeRun);
  }, [activeRun]);

  // Start every batch from a clean slate.
  //
  // This provider stays mounted for the life of the app and only the batch it points at
  // changes, so without this reset each run inherits the last one's state. extensionTriggeredRef
  // is the dangerous one: left at `true`, the extension effect below bails out on every later
  // batch and the user watches all of those listings sit at "pending" until they reload the page.
  useEffect(() => {
    setServerStatus(null);
    setSource('live');
    setExtensionResults({});
    setExtensionProgress(emptyProgress);
    setExtensionBlocked(null);
    setRefusedAsAutomation([]);
    extensionTriggeredRef.current = false;
    queueWokenRef.current = false;
    listingsRefreshedRef.current = false;
    completeModalShownRef.current = false;
    lastSnapshotRef.current = { signature: null, extensionResults: null };
  }, [batchId]);

  // ── Polling ───────────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!batchId) return undefined;

    let stopped = false;
    let interval;

    const applyPayload = (payload) => {
      if (stopped) return;

      if (payload?.status !== 'not_found') {
        setServerStatus(payload);
        setSource('live');
        if (isNewCompletionSighting(activeRunRef.current, payload?.status)) {
          setActiveRun((run) => (run ? { ...run, completionReported: true } : run));
        }
        return;
      }

      // The server has forgotten this batch, so there is nothing left to ask it for. Show the
      // last snapshot we kept of the run, if we have one.
      clearInterval(interval);

      const snapshot = readBatchSnapshot(batchId);
      if (snapshot?.serverStatus) {
        setServerStatus(snapshot.serverStatus);
        // What this session actually saw the extension do beats anything cached earlier.
        setExtensionResults((live) => (
          Object.keys(live).length > 0 ? live : snapshot.extensionResults || {}
        ));
        setSource('snapshot');
        return;
      }

      setServerStatus(payload);
      setSource('expired');
    };

    const fetchStatus = async () => {
      try {
        // Once this active run has already told analytics it completed, every later poll of it —
        // including the first one after a reload — must stay silent about that.
        const silent = Boolean(activeRunRef.current?.completionReported);
        const response = await dispatch(getBatchStatus(batchId, { silent }));
        if (response?.status) applyPayload(response.status);
      } catch (err) {
        console.error('Failed to fetch batch status:', err);
      }
    };

    fetchStatus();

    interval = setInterval(() => {
      if (!TERMINAL_SERVER_STATUSES.includes(serverStatusRef.current?.status)) {
        fetchStatus();
      }
    }, POLL_INTERVAL_MS);

    return () => {
      stopped = true;
      clearInterval(interval);
    };
  }, [batchId, dispatch]);

  // ── Extension work ────────────────────────────────────────────────────────────────────────

  // The batch itself is finished; anything the server marked `pendingExtension` is now this
  // browser's job. Fires once per batch — the reset above is what re-arms it for the next one.
  //
  // A run the seller stopped is never picked up again, on this page load or any later one: the
  // stop is kept with the run itself, so reloading the page cannot restart what they stopped.
  // Nothing is handed to the extension until it has answered, and nothing is claimed if it does
  // not. Claiming first and finding out afterwards was the whole cost of the old behaviour: every
  // listing waited out the full silence budget and was then left holding a claim nobody could
  // finish, where the queued create had to go stale before anything could pick it up again.
  const beginExtensionWork = useCallback(async (pendingItems) => {
    const { available, message } = await askExtensionIfItCanAct();

    if (!available) {
      trackEvent(ANALYTICS_EVENTS.EXTENSION_UNAVAILABLE, {
        surface: 'bulk-cross-list',
        trigger: 'publish',
        extensionMessage: message,
        respondedWithMessage: Boolean(message),
        onMobile: isMobileDevice(),
        waitingItems: pendingItems.length,
      });
      setExtensionBlocked({ waiting: pendingItems.length, message });
      return;
    }

    setExtensionBlocked(null);
    setExtensionProgress({ ...emptyProgress, total: pendingItems.length });
    startExtensionRun(pendingItems);
  }, [startExtensionRun]);

  // Updates the run queued on posts the accounts already held run from the extension's queue, woken once
  // the run is over (fire and forget, as after a save).
  useEffect(() => {
    if (source !== 'live' || serverStatus?.status !== 'completed') return;
    if (isStopped || queueWokenRef.current || !leftUpdatesQueued(serverStatus)) return;
    queueWokenRef.current = true;
    startExtensionQueue();
  }, [source, serverStatus, isStopped]);

  useEffect(() => {
    if (source !== 'live' || serverStatus?.status !== 'completed') return;
    if (isStopped || extensionTriggeredRef.current) return;

    const pendingItems = collectPendingExtensionItems(serverStatus);
    if (pendingItems.length === 0) return;

    // Latched before the answer comes back, so a re-render while the extension is being asked
    // cannot ask it twice. Once it says no, starting again is the seller's own decision.
    extensionTriggeredRef.current = true;
    beginExtensionWork(pendingItems);
  }, [source, serverStatus, isStopped, beginExtensionWork]);

  // "Try again" on the run page: ask the extension once more and start if it answers this time.
  const retryExtensionStart = useCallback(async () => {
    const pendingItems = collectPendingExtensionItems(serverStatusRef.current);
    if (pendingItems.length === 0) {
      setExtensionBlocked(null);
      return;
    }
    await beginExtensionWork(pendingItems);
  }, [beginExtensionWork]);

  // Creates run in this tab, so closing it stops them wherever the user happens to be in the
  // app. A paused run is not creating anything, so it is left to close quietly.
  useEffect(() => {
    if (extensionPhase !== 'processing') return undefined;

    const warnBeforeClosing = (event) => {
      event.preventDefault();
      event.returnValue =
        'Cross-listing is still running. Closing this tab pauses it.';
      return event.returnValue;
    };

    window.addEventListener('beforeunload', warnBeforeClosing);
    return () => window.removeEventListener('beforeunload', warnBeforeClosing);
  }, [extensionPhase]);

  // ── Derived view of the run ───────────────────────────────────────────────────────────────

  const mergedResults = useMemo(
    () => mergeExtensionResults(serverStatus?.results, extensionResults),
    [serverStatus?.results, extensionResults]
  );

  const counts = useMemo(() => countRunResults(mergedResults), [mergedResults]);

  const overallStatus = useMemo(() => {
    if (source === 'expired') return 'expired';
    return deriveRunStatus({
      serverStatus: serverStatus?.status,
      extensionPhase,
      counts,
      failedListings: serverStatus?.failed || 0,
      isStopped,
      // The extension effect below only ever picks work up while this is the live run, so once the
      // server has forgotten the batch and all we have left is the saved copy, what it still owed
      // the browser is not coming — the same answer the rows themselves give.
      isFollowed: source === 'live',
      // Nothing is on its way while the extension is not answering, so the run must not read as
      // working. It is waiting on the seller now, not on the browser.
      extensionUnavailable: Boolean(extensionBlocked),
    });
  }, [source, serverStatus?.status, serverStatus?.failed, extensionPhase, counts, isStopped, extensionBlocked]);

  // The run is over when the batch is done and the extension has nothing left to do — either
  // because everything was created, or because the seller stopped the rest. The phase alone is
  // not enough to decide this: one runner is shared by every batch, so it can read 'done'
  // because the batch *before* this one finished.
  const isRunFinished = serverStatus?.status === 'completed'
    && (counts.totalPending === 0 || isStopped)
    && extensionPhase !== 'processing'
    && extensionPhase !== 'paused';

  // ── Snapshot, so a finished run survives the server forgetting it ─────────────────────────

  useEffect(() => {
    if (!batchId || !serverStatus || source !== 'live') return;

    const signature = [
      serverStatus.status,
      serverStatus.completed,
      serverStatus.failed,
      serverStatus.progress,
      (serverStatus.results || []).length,
      isRunFinished,
      activeRun?.stoppedAt || '',
    ].join('|');

    const previous = lastSnapshotRef.current;
    if (signature === previous.signature && extensionResults === previous.extensionResults) return;

    lastSnapshotRef.current = { signature, extensionResults };
    saveBatchSnapshot(batchId, {
      startedAt: serverStatus.startedAt,
      serverStatus,
      extensionResults,
      // Kept here too, so a page showing this run later still knows the rest was not created on
      // purpose rather than still on its way.
      stoppedAt: activeRun?.stoppedAt || null,
    });
  }, [batchId, serverStatus, extensionResults, source, isRunFinished, activeRun?.stoppedAt]);

  // ── Finishing up ──────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isRunFinished) return;

    if (!listingsRefreshedRef.current) {
      listingsRefreshedRef.current = true;
      dispatch(reloadListingsAfterChange());
    }

    // GlobalOnboardingTips listens for this and congratulates the user on a run that went through
    // with nothing left for them.
    if (!completeModalShownRef.current && isCleanRun(mergedResults)) {
      completeModalShownRef.current = true;
      window.dispatchEvent(new CustomEvent('showCompleteModal'));
    }
  }, [isRunFinished, mergedResults, dispatch]);

  // ── Controls ──────────────────────────────────────────────────────────────────────────────

  const startRun = useCallback((newBatchId, meta = {}) => {
    if (!newBatchId) return;
    setActiveRun({
      batchId: newBatchId,
      submittedAt: Date.now(),
      listingCount: meta.listingCount ?? null,
      platforms: meta.platforms ?? null,
    });
  }, []);

  // Closing the run is only ever about this screen. Stopping extension work in progress is a
  // separate decision the caller makes (and confirms) with cancelExtension.
  const dismissRun = useCallback(() => {
    setActiveRun(null);
  }, []);

  /**
   * Stop creating the rest of this run. The stop is remembered with the run itself, because it
   * has to outlive this page: what the seller stopped must stay stopped after a reload, and the
   * items left over must read as "not created" rather than as still on their way.
   *
   * It is written to the kept copy of the batch right here rather than left to the effect below,
   * because stopping and closing the run is one action for the seller (the floating panel does
   * both at once) and the run it belongs to is gone by the time that effect would run.
   */
  const cancelExtension = useCallback(() => {
    stopExtensionRun();

    const stoppedAt = Date.now();
    setActiveRun((run) => (run ? { ...run, stoppedAt } : run));

    if (batchId && serverStatusRef.current) {
      saveBatchSnapshot(batchId, {
        startedAt: serverStatusRef.current.startedAt,
        serverStatus: serverStatusRef.current,
        extensionResults: extensionResultsRef.current,
        stoppedAt,
      });
    }
  }, [batchId, stopExtensionRun]);

  const retryPlatform = useCallback(
    (listingId, platform) => retryExtensionItem(listingId, platform),
    [retryExtensionItem]
  );

  const retryAllFailed = useCallback(async () => {
    const failed = listRetryableFailures(extensionResultsRef.current);

    // One at a time: the extension can only drive one marketplace session at a time.
    // eslint-disable-next-line no-restricted-syntax
    for (const item of failed) {
      // eslint-disable-next-line no-await-in-loop
      await retryExtensionItem(item.listingId, item.platform);
    }
  }, [retryExtensionItem]);

  /**
   * Look up a batch that is not the active run — for a page showing an earlier run. Read-only:
   * a batch nobody is running is never handed to the extension, and looking one up is not the
   * run finishing, so it reports nothing to analytics.
   *
   * Both records of the run are put together (see composeInspectedRun), because neither is the
   * whole picture: the server never hears about the creates this browser made for it.
   */
  const inspectBatch = useCallback(async (wantedBatchId) => {
    if (!wantedBatchId) return composeInspectedRun(null, null);

    const snapshot = readBatchSnapshot(wantedBatchId);

    let stillOnServer = null;
    try {
      const response = await dispatch(getBatchStatus(wantedBatchId, { silent: true }));
      const status = response?.status;
      if (status && status.status !== 'not_found') stillOnServer = status;
    } catch (err) {
      console.error('Failed to look up batch:', err);
    }

    return composeInspectedRun(stillOnServer, snapshot);
  }, [dispatch]);

  const value = useMemo(() => ({
    activeRun,
    serverStatus,
    source,
    isStopped,
    startedAt: serverStatus?.startedAt || activeRun?.submittedAt || null,
    extension: {
      phase: extensionPhase,
      progress: extensionProgress,
      results: extensionResults,
      isPaused: extensionPaused,
      blocked: extensionBlocked,
      refusedAsAutomation,
    },
    mergedResults,
    counts,
    overallStatus,
    startRun,
    dismissRun,
    retryPlatform,
    retryAllFailed,
    pauseExtension,
    resumeExtension,
    cancelExtension,
    retryExtensionStart,
    inspectBatch,
  }), [
    activeRun,
    serverStatus,
    source,
    isStopped,
    extensionPhase,
    extensionProgress,
    extensionResults,
    extensionPaused,
    extensionBlocked,
    refusedAsAutomation,
    mergedResults,
    counts,
    overallStatus,
    startRun,
    dismissRun,
    retryPlatform,
    retryAllFailed,
    pauseExtension,
    resumeExtension,
    cancelExtension,
    retryExtensionStart,
    inspectBatch,
  ]);

  return (
    <BulkCrossListRunContext.Provider value={value}>{children}</BulkCrossListRunContext.Provider>
  );
}

BulkCrossListRunProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
